document.documentElement.classList.remove('no-js');

function closeSubmenu(toggle, restoreFocus = false) {
  const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!submenu) return;

  toggle.setAttribute('aria-expanded', 'false');
  submenu.hidden = true;
  submenu.querySelectorAll('[data-submenu-toggle][aria-expanded="true"]').forEach((childToggle) => closeSubmenu(childToggle));
  if (restoreFocus) toggle.focus();
}

function initSubmenus(root = document) {
  root.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
    if (toggle.dataset.initialized === 'true') return;

    const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!submenu) return;
    toggle.dataset.initialized = 'true';

    toggle.addEventListener('click', () => {
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';

      if (willOpen) {
        const siblingList = toggle.closest('ul');
        siblingList?.querySelectorAll(':scope > li > .nav-tree__row > [data-submenu-toggle][aria-expanded="true"]').forEach((siblingToggle) => {
          if (siblingToggle !== toggle) closeSubmenu(siblingToggle);
        });
        toggle.setAttribute('aria-expanded', 'true');
        submenu.hidden = false;
      } else {
        closeSubmenu(toggle);
      }
    });

    toggle.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && toggle.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        submenu.querySelector('a, button')?.focus();
      }
    });

    submenu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeSubmenu(toggle, true);
      }
    });
  });
}

function initMobileMenus(root = document) {
  root.querySelectorAll('[data-menu-toggle]').forEach((menuButton) => {
    if (menuButton.dataset.initialized === 'true') return;

    const mobileMenu = document.getElementById(menuButton.getAttribute('aria-controls'));
    if (!mobileMenu) return;
    menuButton.dataset.initialized = 'true';

    const closeMenu = (restoreFocus = false) => {
      menuButton.setAttribute('aria-expanded', 'false');
      mobileMenu.hidden = true;
      document.body.classList.remove('menu-open');
      mobileMenu.querySelectorAll('[data-submenu-toggle][aria-expanded="true"]').forEach((toggle) => closeSubmenu(toggle));
      if (restoreFocus) menuButton.focus();
    };

    const openMenu = () => {
      const headerBottom = menuButton.closest('.site-header')?.getBoundingClientRect().bottom;
      if (headerBottom) mobileMenu.style.setProperty('--header-bottom', `${Math.max(0, headerBottom)}px`);
      menuButton.setAttribute('aria-expanded', 'true');
      mobileMenu.hidden = false;
      document.body.classList.add('menu-open');
      mobileMenu.querySelector('a, button')?.focus();
    };

    menuButton.addEventListener('click', () => {
      if (menuButton.getAttribute('aria-expanded') === 'true') closeMenu();
      else openMenu();
    });

    mobileMenu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
        return;
      }

      if (event.key === 'Tab') {
        const focusable = [...mobileMenu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')].filter((element) => !element.closest('[hidden]'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    document.addEventListener('click', (event) => {
      if (menuButton.getAttribute('aria-expanded') !== 'true') return;
      if (!mobileMenu.contains(event.target) && !menuButton.contains(event.target)) closeMenu();
    });

    const desktopBreakpoint = window.matchMedia('(min-width: 1121px)');
    const handleBreakpoint = (event) => {
      if (event.matches) closeMenu();
    };
    if (desktopBreakpoint.addEventListener) desktopBreakpoint.addEventListener('change', handleBreakpoint);
    else desktopBreakpoint.addListener(handleBreakpoint);
  });
}

function initProductRecommendations(root = document) {
  root.querySelectorAll('product-recommendations[data-url]').forEach((element) => {
    if (element.dataset.initialized === 'true') return;
    element.dataset.initialized = 'true';

    fetch(element.dataset.url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const source = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
        if (source?.innerHTML.trim()) element.innerHTML = source.innerHTML;
        else element.remove();
      })
      .catch(() => element.remove());
  });
}

function initQuantityControls(root = document) {
  root.querySelectorAll('[data-quantity]').forEach((wrapper) => {
    if (wrapper.dataset.initialized === 'true') return;

    const input = wrapper.querySelector('input');
    if (!input) return;
    wrapper.dataset.initialized = 'true';

    wrapper.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        const minimum = Number(input.min || 1);
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const increment = Number(input.step || 1);
        const direction = Number(button.dataset.step || 0);
        const next = Number(input.value || minimum) + increment * direction;
        input.value = Math.min(maximum, Math.max(minimum, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
}

function updateCartIndicators(cart) {
  document.querySelectorAll('[data-cart-count]').forEach((count) => {
    count.textContent = cart.item_count;
    count.classList.toggle('cart-count--empty', cart.item_count === 0);
  });

  document.querySelectorAll('[data-cart-link]').forEach((link) => {
    link.setAttribute('aria-label', `${link.dataset.cartLabel}, ${cart.item_count}`);
  });
}

function initProductForms(root = document) {
  root.querySelectorAll('[data-product-form]').forEach((form) => {
    if (form.dataset.initialized === 'true') return;

    const productInfo = form.closest('[data-product-info]');
    const productPage = productInfo?.closest('.product-page');
    const select = productInfo?.querySelector('[data-variant-select]');
    const variantInput = form.querySelector('[data-variant-id]');
    const button = form.querySelector('[type="submit"][name="add"]');
    const message = form.querySelector('[data-product-form-message]');
    const priceContainer = productInfo?.querySelector('[data-product-price]');
    const skuContainer = productInfo?.querySelector('[data-product-sku]');
    const quantityInput = productInfo?.querySelector('[name="quantity"]');
    const gallery = productPage?.querySelector('[data-product-gallery]');
    if (!variantInput || !button) return;

    form.dataset.initialized = 'true';

    const selectedOption = () => select?.options[select.selectedIndex];

    const updateVariant = (updateUrl = false) => {
      const option = selectedOption();
      if (!option) return;

      const isAvailable = option.dataset.available === 'true';
      variantInput.value = option.value;
      button.disabled = !isAvailable;
      button.textContent = isAvailable ? button.dataset.addText : button.dataset.soldText;

      if (priceContainer && option.dataset.price) {
        const price = document.createElement('div');
        price.className = 'price';
        const currentPrice = document.createElement('span');
        currentPrice.textContent = option.dataset.price;
        price.append(currentPrice);
        if (option.dataset.comparePrice) {
          const comparePrice = document.createElement('s');
          comparePrice.textContent = option.dataset.comparePrice;
          price.append(comparePrice);
        }
        priceContainer.replaceChildren(price);
      }

      if (skuContainer) {
        const skuValue = skuContainer.querySelector('span');
        if (skuValue) skuValue.textContent = option.dataset.sku || '';
        skuContainer.classList.toggle('product-sku--empty', !option.dataset.sku);
      }

      if (quantityInput) {
        const minimum = Number(option.dataset.quantityMin || 1);
        const increment = Number(option.dataset.quantityStep || 1);
        quantityInput.min = String(minimum);
        quantityInput.step = String(increment);
        if (option.dataset.quantityMax) quantityInput.max = option.dataset.quantityMax;
        else quantityInput.removeAttribute('max');
        if (Number(quantityInput.value) < minimum) quantityInput.value = String(minimum);
      }

      if (gallery && option.dataset.mediaId) {
        const targetMedia = gallery.querySelector(`[data-media-id="${CSS.escape(option.dataset.mediaId)}"]`);
        if (targetMedia) {
          gallery.querySelectorAll('[data-media-id]').forEach((media) => media.classList.remove('media--selected'));
          targetMedia.classList.add('media--selected');
          gallery.prepend(targetMedia);
        }
      }

      if (message) {
        message.textContent = '';
        message.setAttribute('role', 'status');
        message.classList.remove('product-form__message--error', 'product-form__message--success');
      }

      if (updateUrl && option.dataset.url && window.history.replaceState) window.history.replaceState({}, '', option.dataset.url);
    };

    if (select) {
      select.addEventListener('change', () => updateVariant(true));
      updateVariant(false);
    }

    form.addEventListener('submit', async (event) => {
      if (event.submitter && event.submitter !== button) return;
      event.preventDefault();

      const option = selectedOption();
      const shouldEnableAfterRequest = option ? option.dataset.available === 'true' : true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.dataset.loadingText;
      if (message) message.textContent = '';

      try {
        const response = await fetch(`${form.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
          credentials: 'same-origin',
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || message?.dataset.errorText);

        const cartResponse = await fetch(`${form.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (cartResponse.ok) updateCartIndicators(await cartResponse.json());

        if (message) {
          message.setAttribute('role', 'status');
          const text = document.createElement('span');
          text.textContent = message.dataset.successText;
          const link = document.createElement('a');
          link.href = form.dataset.cartUrl;
          link.textContent = message.dataset.viewCartText;
          message.replaceChildren(text, document.createTextNode(' '), link);
          message.classList.add('product-form__message--success');
        }
      } catch (error) {
        if (message) {
          message.textContent = error.message || message.dataset.errorText;
          message.classList.add('product-form__message--error');
          message.setAttribute('role', 'alert');
        }
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = !shouldEnableAfterRequest;
        button.textContent = shouldEnableAfterRequest ? button.dataset.addText : button.dataset.soldText;
      }
    });
  });
}

function initCartForms(root = document) {
  root.querySelectorAll('[data-cart-form]').forEach((form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const updateButton = form.querySelector('[data-cart-update]');
    const status = form.querySelector('[data-cart-status]');

    form.addEventListener('submit', (event) => {
      if (event.submitter !== updateButton) return;
      updateButton.disabled = true;
      updateButton.textContent = updateButton.dataset.loadingText;
      if (status) status.textContent = updateButton.dataset.loadingText;
    });
  });
}

function initTheme(root = document) {
  initSubmenus(root);
  initMobileMenus(root);
  initQuantityControls(root);
  initProductForms(root);
  initCartForms(root);
  initProductRecommendations(root);
}

document.addEventListener('DOMContentLoaded', () => initTheme());
document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
